import { Suspense } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function ElectricShowerPage() {
  useEffect(() => {
    document.title = 'Electric Shower Installation Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional electric shower installation in Watford. Safe and certified shower installations by NICEIC electricians. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/electric-shower",
      "areaServed": "Watford",
      "serviceType": "Electric Shower Installation",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
                <i className="ri-map-pin-line text-amber-500"></i>
                <span className="text-sm font-medium text-gray-700">Serving Watford &amp; Surrounding Areas</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Electric Shower Installation <span className="text-amber-500">in Watford</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional electric shower installation and replacement services. From new installations to upgrades, we provide safe, certified work throughout Watford and Rickmansworth.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                  Call 020 3198 1818
                </a>
                <button onClick={scrollToContact} className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                  Get Free Quote
                </button>
              </div>
              
              <div className="flex flex-wrap gap-6 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">NICEIC Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">15 Years Experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">Part P Compliant</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Electric Shower Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Complete installation and replacement services for all electric shower types
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-add-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">New Installations</h3>
                <p className="text-gray-600 leading-relaxed">
                  Complete electric shower installation including dedicated circuit, RCD protection, and water supply connection.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Shower Replacements</h3>
                <p className="text-gray-600 leading-relaxed">
                  Replace old or faulty electric showers with modern, energy-efficient models with improved performance.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-arrow-up-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Power Upgrades</h3>
                <p className="text-gray-600 leading-relaxed">
                  Upgrade to higher kilowatt showers for better water pressure and temperature control.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-flashlight-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Circuit Installation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Dedicated shower circuit from consumer unit with appropriate cable sizing and RCD protection.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Fault Finding &amp; Repair</h3>
                <p className="text-gray-600 leading-relaxed">
                  Diagnose and repair electric shower faults including heating elements and thermostats.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Certification</h3>
                <p className="text-gray-600 leading-relaxed">
                  All work certified with electrical installation certificate for Building Regulations compliance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Shower Power Guide */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Choosing the Right Power Rating</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Higher kilowatt ratings provide better flow and temperature
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
                <div className="text-4xl font-bold text-amber-500 mb-2">7.5-8.5kW</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Standard Power</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Suitable for most homes with adequate water pressure. Good for single bathroom use.
                </p>
                <ul className="text-sm text-gray-600 space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Lower running costs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Standard cable required</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-amber-100 text-center ring-2 ring-amber-500">
                <div className="inline-block bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-2">POPULAR</div>
                <div className="text-4xl font-bold text-amber-500 mb-2">9.5-10.5kW</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">High Power</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Better flow rate and temperature stability. Ideal for family homes and multiple bathrooms.
                </p>
                <ul className="text-sm text-gray-600 space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Excellent performance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Consistent temperature</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
                <div className="text-4xl font-bold text-amber-500 mb-2">10.8kW+</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Maximum Power</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Premium performance with powerful flow. Perfect for low water pressure areas.
                </p>
                <ul className="text-sm text-gray-600 space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Maximum flow rate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Best for large families</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Electric Showers */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Benefits of Electric Showers</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Why electric showers are a popular choice for UK homes
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-water-flash-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Instant Hot Water</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No waiting for water to heat up. Hot water on demand whenever you need it.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-money-pound-circle-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Energy Efficient</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Only heats water when you use it, reducing energy waste and lowering bills.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-home-gear-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Independent System</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Works independently from your boiler, so you can shower even if heating is off.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-drop-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Consistent Temperature</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Modern thermostatic controls maintain steady temperature even if water pressure varies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Installation Requirements */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What&apos;s Included in Installation</h2>
              <p className="text-xl text-gray-600">
                Professional installation with all necessary electrical work
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Dedicated circuit installation from consumer unit with appropriate MCB and RCD protection</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Correct cable sizing based on shower power rating and cable run length</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Shower unit mounting and secure fixing to wall with appropriate backing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Water supply connection with isolation valve for easy maintenance</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Full testing including insulation resistance and earth continuity tests</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Electrical Installation Certificate for Building Regulations compliance</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 leading-relaxed">Demonstration of controls and operation instructions</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Beyond Electrical?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Expert electric shower installation in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-award-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">15 Years Experience</h3>
                <p className="text-gray-600 leading-relaxed">
                  Hundreds of electric shower installations completed across Watford and Hertfordshire.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Part P Certified</h3>
                <p className="text-gray-600 leading-relaxed">
                  All bathroom electrical work certified and compliant with Part P Building Regulations.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-tools-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Professional Service</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clean, tidy work with minimal disruption. We respect your home and clean up after ourselves.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Areas Covered */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Areas We Cover</h2>
              <div className="flex flex-wrap justify-center gap-4 text-gray-700">
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Watford</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Rickmansworth</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Bushey</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Abbots Langley</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Croxley Green</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Chorleywood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Northwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Radlett</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Borehamwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">St Albans</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Hemel Hempstead</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Harrow</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact" className="py-20 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Need an Electric Shower Installed?</h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get a free quote for electric shower installation in Watford
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}